/**
 * useAuth Hook
 * 
 * Feature: 033-auth-complete
 * Description: Custom hook for authentication operations
 * Constitution: v1.11.0
 */

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuthStore } from '../../stores/authStore';
import { loginRequest, apiTokenRequest } from '../config';
import { isDevAuthBypass } from '../../authConfig';
import axios from 'axios';

/**
 * Dev-mode mock — same shape as the real hook but no MSAL dependency.
 */
const useDevAuth = () => {
  const { user, isAuthenticated, isLoading, error } = useAuthStore();

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: async () => { /* no-op in dev */ },
    logout: async () => { window.location.href = '/login'; },
    getAccessToken: async () => 'dev-token',
  };
};

export const useAuth = () => {
  // In dev bypass mode, skip MSAL entirely
  if (isDevAuthBypass) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useDevAuth();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { instance, accounts } = useMsal();
  const { user, isAuthenticated, isLoading, error, setUser, setPermissions, logout: logoutStore } = useAuthStore();

  /**
   * Login with redirect to Microsoft Entra ID
   */
  const login = useCallback(async (loginHint?: string) => {
    try {
      const request = loginHint
        ? { ...loginRequest, loginHint, prompt: 'login' as const }
        : loginRequest;
      await instance.loginRedirect(request);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    try {
      // Call backend to end session
      await axios.delete('/api/auth/session');

      // Clear local state
      logoutStore();

      // Logout from MSAL
      await instance.logoutRedirect({
        account: accounts[0],
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Logout anyway even if backend call fails
      logoutStore();
      await instance.logoutRedirect();
    }
  }, [instance, accounts, logoutStore]);

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      if (accounts.length === 0) {
        return null;
      }

      const response = await instance.acquireTokenSilent({
        scopes: apiTokenRequest.scopes,
        account: accounts[0],
      });

      return response.idToken; // FCM model — send the ID token (see auth/config.ts)
    } catch (error) {
      console.error('Token acquisition error:', error);
      return null;
    }
  }, [instance, accounts]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    getAccessToken,
  };
};
